import React from 'react';
import { AlgoRerunDto, Consortium, Sample, Roles } from '@cambridgeassessment/lib-ee-commons';
import { Subheader, Icons, Button, Table } from '@cambridgeassessment/cambridge-ui';

import {
  LinkWrapper,
  SampleCardSummary,
  StyledSubtitle,
  StyledTitle,
  SummaryTextParagraphs,
  TitleButtonWrapper,
  TitleIconWrapper,
} from '../ManageSampleChanges.styles';

import { SortTypes, resolveSortOrder, sortLearners, TableSortConfig } from '../helpers';
import { SampleSearchParams } from '../../../../store/samplesManagement/types';
import { ConsortiumCentres } from '../ConsortiumCentres/ConsortiumCentres';
import { Entry } from '@cambridgeassessment/lib-ee-commons/src/domain/db/entries';
import { Link } from '@material-ui/core';
import { AlgoRerunAuditTable } from '../AlgoRerun/AlgoRerunAuditTable';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';

interface Props {
  sample: Sample;
  onAddClick: () => void;
  inputsStates: SampleSearchParams | undefined;
  consortium: Consortium | undefined;
  isLoading: boolean;
  sortConfig: TableSortConfig | undefined;
  setSortConfig: (val: TableSortConfig | undefined) => void;
  onReplaceClick: (clickedLearner: Entry) => void;
  onRerunClick: () => void;
  algoReruns: AlgoRerunDto[];
}

export const SampleCardContent = (props: Props): JSX.Element => {
  const {
    isLoading,
    sample,
    onAddClick,
    inputsStates,
    consortium,
    sortConfig,
    setSortConfig,
    onReplaceClick,
    onRerunClick,
    algoReruns,
  } = props;

  const {
    userData: { selectedRole },
  } = useSelector((state: RootState) => state);

  sortLearners(sample.wholeSample ?? [], sortConfig);

  const noEntriesInSample = sample.wholeSample?.length === 0;

  return (
    <SampleCardSummary>
      <TitleButtonWrapper>
        <StyledTitle>
          {!noEntriesInSample && (
            <div data-qa-id='found-sample-title'>
              {sample.assessment.code} - {sample.assessment.name} - {sample.componentName} {sample.componentNumber}
            </div>
          )}
        </StyledTitle>
        <div>
          <Button
            qaId='rerun-sample-btn'
            size='small'
            variant='outlined'
            onClick={onRerunClick}
            disabled={![Roles.SUBMIT_SUPER_ADMIN, Roles.SUBMIT_ADMINISTRATOR].includes(selectedRole?.name)}
          >
            Re-run sample selection
          </Button>
        </div>
      </TitleButtonWrapper>
      <SummaryTextParagraphs>
        <StyledSubtitle data-qa-id='found-sample-qualification-name-subtitle'>
          {!noEntriesInSample && sample.qualificationName}
        </StyledSubtitle>
        <Subheader data-qa-id='candidates-included-in-sample-subheader'>
          Candidates listed below are <b>included</b> in the sample request.
        </Subheader>
        <ConsortiumCentres consortium={consortium} componentNumber={inputsStates?.componentNumber ?? ''} />
        <Button
          size='small'
          variant='outlined'
          startIcon='+'
          qaId='add-candidates-btn'
          onClick={(e) => {
            e.stopPropagation();
            onAddClick();
          }}
        >
          Add additional candidates
        </Button>
      </SummaryTextParagraphs>

      <Table<Entry>
        isLoading={isLoading}
        data={sample.wholeSample ?? []}
        columns={[
          {
            renderHeaderCell: function render() {
              return (
                <TitleIconWrapper data-qa-id='header-learner-number-and-name-table'>
                  <div>Candidate number and name</div>
                  <Icons.Arrows selected={sortConfig?.sortField === SortTypes.CandidateNr} />
                </TitleIconWrapper>
              );
            },
            renderCell: function renderCol(item) {
              return (
                <div data-qa-id={`learner-number-${item.learner.number}-cell`}>{`${item.learner.number
                  .toString()
                  .padStart(4, '0')} - ${item.learner.name}`}</div>
              );
            },
            onHeaderClick: () => {
              resolveSortOrder(SortTypes.CandidateNr, setSortConfig, sortConfig);
            },
          },
          {
            renderHeaderCell: function render() {
              return (
                <TitleIconWrapper data-qa-id='header-learner-mark-table'>
                  <div>Mark</div>
                  <Icons.Arrows selected={sortConfig?.sortField === SortTypes.Mark} />
                </TitleIconWrapper>
              );
            },
            renderCell: function renderCol(item) {
              return (
                <div data-qa-id={`learner-mark-${item.learner.number}-mark-cell`}>
                  {item.mark}/{sample.maxMark}
                </div>
              );
            },
            onHeaderClick: () => {
              resolveSortOrder(SortTypes.Mark, setSortConfig, sortConfig);
            },
            align: 'left',
          },
          {
            renderHeaderCell: function render() {
              return (
                consortium && (
                  <TitleIconWrapper data-qa-id='header-learner-centre-table'>
                    <div>Centre</div>
                    <Icons.Arrows selected={sortConfig?.sortField === SortTypes.Centre} />
                  </TitleIconWrapper>
                )
              );
            },
            renderCell: function renderCol(item) {
              return <div data-qa-id='learner-centre-cell'>{consortium && item.centreId}</div>;
            },
            onHeaderClick: consortium ? () => resolveSortOrder(SortTypes.Centre, setSortConfig, sortConfig) : undefined,
            align: 'left',
          },
          {
            align: 'right',
            renderCell: function renderCol(item) {
              return (
                <LinkWrapper>
                  <Link data-qa-id={`replace-btn-${item.learner.number}`} onClick={() => onReplaceClick(item)}>
                    Replace
                  </Link>
                </LinkWrapper>
              );
            },
          },
        ]}
      />
      {algoReruns.length > 0 && <AlgoRerunAuditTable rerunsData={algoReruns} />}
    </SampleCardSummary>
  );
};
